import { useEffect, useState } from 'react'
import { FieldErrors, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import BillingConfigForm from './BillingConfigForm'
import { BillingConfigFormValues, billingConfigSchema, defaultFormValues, formValuesToPayload } from './schema'

import styles from './BillingConfigEditor.module.scss'

import { isApiError } from '@/api'
import { EnterpriseListItem } from '@/api/generated'
import { createBillingConfig } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { captureEvent } from '@/utils/captureEvent'
import EnterpriseSearch from '@/pages/app/Enterprises/components/EnterpriseSearch'
import { EventName } from '@/utils/eventDefinitions'

interface CreateBillingConfigDialogProps {
  isOpen: boolean
  onSave: () => void
  onCancel: () => void
}

const CreateBillingConfigDialog = ({ isOpen, onSave, onCancel }: CreateBillingConfigDialogProps) => {
  const [selectedEnterprise, setSelectedEnterprise] = useState<EnterpriseListItem | null>(null)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<BillingConfigFormValues>({
    resolver: zodResolver(billingConfigSchema),
    defaultValues: defaultFormValues,
  })

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      reset(defaultFormValues)
      setSelectedEnterprise(null)
      setError(null)
    }
  }, [isOpen, reset])

  const createMutation = useMutation({
    mutationFn: createBillingConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingConfigs'] })
      if (selectedEnterprise) {
        captureEvent(EventName.BillingConfigCreated, {
          enterprise_id: selectedEnterprise.id,
          enterprise_name: selectedEnterprise.name,
        })
      }
      reset()
      onSave()
    },
    onError: (err) => {
      console.error('Failed to create billing config:', err)
      if (isApiError(err)) {
        let detail: string
        try {
          detail = typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail)
        } catch {
          detail = 'Failed to create billing config'
        }
        setError(detail || 'Failed to create billing config')
      } else {
        setError('Failed to create billing config')
      }
    },
  })

  const handleCancel = () => {
    setSelectedEnterprise(null)
    setError(null)
    reset()
    onCancel()
  }

  const onInvalid = (errs: FieldErrors<BillingConfigFormValues>) => {
    const msgs = Object.entries(errs)
      .map(([key, val]) => `${key}: ${val?.message || 'invalid'}`)
      .join(', ')
    setError(`Validation failed: ${msgs}`)
  }

  const onSubmit = (values: BillingConfigFormValues) => {
    setError(null)

    if (!selectedEnterprise) {
      setError('Please select an enterprise')
      return
    }

    createMutation.mutate({
      enterprise_id: selectedEnterprise.id,
      ...formValuesToPayload(values),
    })
  }

  return (
    <DialogWrapper isOpen={isOpen} title="Create Billing Config" close={handleCancel} minWidth={640}>
      <form className={styles.dialogBody} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        {/* Enterprise selector */}
        <div className={styles.formGroup}>
          {selectedEnterprise ? (
            <div className={styles.selectedEnterprise}>
              <label className={styles.label}>Enterprise</label>
              <div className={styles.selectedValue}>
                <div className={styles.enterpriseInfo}>
                  <span>{selectedEnterprise.name}</span>
                  <span className={styles.hint}>ID: {selectedEnterprise.id}</span>
                </div>
                <Button type="link" onClick={() => setSelectedEnterprise(null)} title="x" />
              </div>
            </div>
          ) : (
            <EnterpriseSearch onSelect={setSelectedEnterprise} onError={setError} />
          )}
        </div>

        <BillingConfigForm control={control} errors={errors} disabled={createMutation.isPending} setValue={setValue} />

        <div className={styles.dialogFooter}>
          <Button title="Cancel" onClick={handleCancel} type="light" />
          <Button
            title={createMutation.isPending ? 'Creating...' : 'Create'}
            onClick={handleSubmit(onSubmit, onInvalid)}
            type="dark"
            isDisabled={createMutation.isPending}
          />
        </div>
      </form>
    </DialogWrapper>
  )
}

export default CreateBillingConfigDialog
